import { createContext, type MouseEvent, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { withConsoleBase, withoutConsoleBase } from '../config';

interface RouterValue {
  navigate(path: string, replace?: boolean, options?: { preserveScroll?: boolean }): void;
  pathname: string;
  search: string;
}

const RouterContext = createContext<RouterValue | null>(null);
const navigationGuards = new Set<() => boolean>();
export function guardNavigation(guard: () => boolean) {
  navigationGuards.add(guard);
  return () => { navigationGuards.delete(guard); };
}
function mayNavigate() { return [...navigationGuards].every(guard => guard()); }

function currentPath(): string {
  return withoutConsoleBase(window.location.pathname).replace(/\/+$/, '') || '/';
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState(() => ({ pathname: currentPath(), search: window.location.search }));
  const { pathname, search } = location;
  useEffect(() => {
    let currentIndex = window.history.state?.consoleIndex ?? 0;
    window.history.replaceState({ ...window.history.state, consoleIndex: currentIndex }, '');
    let restoring = false;
    const update = () => {
      const nextIndex = window.history.state?.consoleIndex;
      if (restoring) { restoring = false; return; }
      if (!mayNavigate()) {
        if (typeof nextIndex === 'number' && nextIndex !== currentIndex) {
          restoring = true; window.history.go(currentIndex - nextIndex);
        }
        return;
      }
      currentIndex = nextIndex ?? currentIndex;
      setLocation({ pathname: currentPath(), search: window.location.search });
    };
    const remember = () => { currentIndex = window.history.state?.consoleIndex ?? currentIndex; };
    window.addEventListener('popstate', update);
    window.addEventListener('console:navigated', remember);
    return () => { window.removeEventListener('popstate', update); window.removeEventListener('console:navigated', remember); };
  }, []);
  const value = useMemo<RouterValue>(() => ({
    navigate(path, replace = false, options = {}) {
      if (!mayNavigate()) return;
      const normalized = path.startsWith('/') ? path : '/';
      const consoleIndex = (window.history.state?.consoleIndex ?? 0) + (replace ? 0 : 1);
      window.history[replace ? 'replaceState' : 'pushState']({ consoleIndex }, '', withConsoleBase(normalized));
      window.dispatchEvent(new Event('console:navigated'));
      setLocation({ pathname: currentPath(), search: window.location.search });
      if (!options.preserveScroll) window.scrollTo({ top: 0, behavior: 'auto' });
    },
    pathname, search
  }), [pathname, search]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (!value) throw new Error('useRouter must be used within RouterProvider');
  return value;
}

export function NavLink({ children, className, end = false, onNavigate, to, active: activeOverride }: {
  children: ReactNode;
  className?: string | ((active: boolean) => string);
  end?: boolean;
  onNavigate?(): void;
  to: string;
  active?: boolean;
}) {
  const { navigate, pathname } = useRouter();
  const targetPath = to.split(/[?#]/)[0];
  const active = activeOverride ?? (end ? pathname === targetPath : pathname === targetPath || pathname.startsWith(targetPath + '/'));
  function follow(event: MouseEvent<HTMLAnchorElement>) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(to);
    onNavigate?.();
  }
  return <a aria-current={active ? 'page' : undefined} className={typeof className === 'function' ? className(active) : className} href={withConsoleBase(to)} onClick={follow}>{children}</a>;
}
